import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { orderBy } from 'lodash'

import { AnalyticsPerformance } from './utils/types'
import { DuckDBSchemaType } from '@/services/duckdb/utils/types'
import { AnalyticsField, FilterField } from '@types'

import { ANALYTICS_CAPTIONS } from '../utils/const'
import { ASSET_FIELD } from '@/const'

import { useDuckDB } from '@/services/duckdb'

import { getCurrentDate } from '@/helpers/dates'
import { getFilterTags, getFilterValues } from '../utils/helpers'

import useAnalyticsStore from '../index'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useTagsBunchStore } from '@/store/tags/bunch'

const TABLE_NAME = 'analytics_performance'

const TABLE_SCHEMA: Record<string, DuckDBSchemaType> = {
  date_right: 'DATE',
  account_id: 'VARCHAR',
  asset_id: 'VARCHAR',
  tags: 'VARCHAR[]',
}

export default defineStore('analytics-performance', () => {
  // INIT
  const { pushData, createTable } = useDuckDB()

  const analyticsStore = useAnalyticsStore()

  const list = ref<Record<string, AnalyticsPerformance[]>>()

  const loading = ref(false)
  const deprecated = ref<boolean>()

  // GETTERS

  const isInit = computed(() => deprecated.value !== undefined)

  const getList = computed(() => {
    if (analyticsStore.getError) return {}
    return list.value
  })

  const getDatesRange = computed(() => {
    return list.value && Object.keys(list.value)
  })

  // SETTERS

  const setList = async (data: {
    performance_view: Record<string, AnalyticsPerformance[]>
  }) => {
    list.value = data.performance_view
    deprecated.value = false
    await createTable(TABLE_NAME, TABLE_SCHEMA)
    await Promise.all(
      Object.values(data.performance_view).map(performance =>
        pushData(TABLE_NAME, performance, TABLE_SCHEMA),
      ),
    )
  }

  // ACTIONS

  const clear = () => {
    list.value = undefined
    loading.value = false
    deprecated.value = undefined
  }

  return {
    isLoading: loading,
    isDeprecated: deprecated,

    isInit,
    getList,
    getDatesRange,

    setList,

    fetch,

    clear,
  }
})

export const useAnalyticsPerformanceFilters = () => {
  const analyticsStore = useAnalyticsStore()
  const assetsBunchStore = useAssetsBunchStore()
  const tagsBunchStore = useTagsBunchStore()

  const analyticsDate = computed(() => analyticsStore.getDate || getCurrentDate({}))

  const getValues = getFilterValues<AnalyticsPerformance>(TABLE_NAME, analyticsDate, assetsBunchStore, 'date_right')
  const getTags = getFilterTags(TABLE_NAME, analyticsDate, tagsBunchStore, 'date_right')

  const filters = computed(() => {
    const result: FilterField[] = [
      {
        name: AnalyticsField.ACCOUNT_ID,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT_ID],
        getter: () => getValues(AnalyticsField.ACCOUNT_ID, ASSET_FIELD.NAME)
      },
      {
        name: AnalyticsField.ACCOUNT_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT_TYPE],
        getter: () => getValues(AnalyticsField.ACCOUNT_ID, ASSET_FIELD.TYPE)
      },
      {
        name: AnalyticsField.ASSET_ID,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET_ID],
        getter: () => getValues(AnalyticsField.ASSET_ID, ASSET_FIELD.NAME)
      },
      {
        name: AnalyticsField.ASSET_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET_TYPE],
        getter: () => getValues(AnalyticsField.ASSET_ID, ASSET_FIELD.TYPE)
      },
    ]
    tagsBunchStore.getMetricTags?.forEach(name => {
      result.push({
        name,
        caption: name,
        getter: () => getTags(name)
      })
    })
    return orderBy(result, 'caption')
  })

  return {
    filters,
  }
}
